import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import PubNub from 'pubnub';
import moment from 'moment';
import {
  FiArrowLeft, FiActivity, FiUsers, FiAward, FiBarChart2, FiTrendingUp,
  FiFilter, FiBell, FiSearch, FiX, FiChevronDown, FiRefreshCw,
  FiEye, FiShield, FiGrid, FiSend, FiPlay, FiSquare, FiAlertCircle,
  FiCheckCircle, FiMessageSquare, FiWifiOff, FiClock,
} from 'react-icons/fi';
import { adminApi, closuresApi, messagesApi, attendanceApi } from '../../services/attendance';
import config from '../../config';

// ─── Constants ────────────────────────────────────────────────────────────────
const ALL_INSURERS = ['max','icici','reliance','abhi','absli','mchi','lic','aegon','ppmc','tata','hdfc','kotak','icicilbd','starhealth','bajaj','godigit'];
const ALL_INSURERS_IN_MIS = ['abhi','absli','aegon','bajaj','godigit','hdfc','icici','icicilbd','kotak','lic','max','mchi','ppmc','reliance','starhealth','tata'];
const BREAK_REASONS = ['Lunch', 'Personal', 'Technical issue', 'Meeting', 'Prayer', 'Other'];

const INSURER_COLORS = {
  max: '#3b82f6', icici: '#f97316', reliance: '#06b6d4', abhi: '#a855f7',
  absli: '#ec4899', mchi: '#22c55e', lic: '#10b981', aegon: '#f59e0b',
  ppmc: '#ef4444', tata: '#6366f1', hdfc: '#dc2626', kotak: '#eab308',
  icicilbd: '#8b5cf6', starhealth: '#14b8a6', bajaj: '#f43f5e', godigit: '#0ea5e9',
};

const CATEGORIES = ['WIP', 'Completed', 'Insurance Actionable', 'Under QC', 'Other'];
const CAT_COLORS = {
  'WIP':                  { text: '#fbbf24', bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.2)'   },
  'Completed':            { text: '#34d399', bg: 'rgba(52,211,153,0.1)',   border: 'rgba(52,211,153,0.2)'   },
  'Insurance Actionable': { text: '#f87171', bg: 'rgba(248,113,113,0.1)', border: 'rgba(248,113,113,0.2)' },
  'Under QC':             { text: '#a78bfa', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.2)' },
  'Other':                { text: '#94a3b8', bg: 'rgba(51,65,85,0.3)',     border: '#334155'                },
};

const STATUS_CATEGORY = {
  'Non Contactable': 'WIP', 'Non-Contactable': 'WIP', 'NON-CONTACTABLE': 'WIP',
  'Calling in progress': 'WIP', 'CALLING IN PROGRESS': 'WIP',
  'Call back requested': 'WIP', 'CALL BACK': 'WIP', 'Call Back': 'WIP',
  'DISCONNECTED': 'WIP', 'Disconnected': 'WIP', 'Video Call Attempted': 'WIP',
  'Call Attempts in progress': 'WIP', 'Unattempted': 'WIP', 'Call Later': 'WIP',
  'NRI': 'WIP', 'Tu Not Required': 'WIP', 'WIP': 'WIP', 'Language Preference': 'WIP',
  '*': 'WIP', 'Not Contactable': 'WIP', 'NON CONTACTABLE': 'WIP',
  'Pending': 'WIP', 'Call Attempt in Progress': 'WIP',
  'Completed': 'Completed', 'Medicals Done': 'Completed', 'Closed': 'Completed',
  'Video FMR Done': 'Completed', 'MEDICAL REPORT UPLOAD': 'Completed',
  'Approved': 'Completed', 'FMR Done': 'Completed', 'Done': 'Completed', 'COMPLETED': 'Completed',
  'Rejected': 'Insurance Actionable', 'Insurer Actionable': 'Insurance Actionable',
  'Insurance Actionable': 'Insurance Actionable', 'INSURER ACTIONABLE': 'Insurance Actionable',
  'Cancelled By Insurer': 'Insurance Actionable', 'Cancelled by Insurer': 'Insurance Actionable',
  'NOT INTERESTED': 'Insurance Actionable', 'Not Interested': 'Insurance Actionable',
  'MEDICAL CANCELLED BY INSURER': 'Insurance Actionable', 'Duplicate Case': 'Insurance Actionable',
  'Tu already done': 'Insurance Actionable', 'Communication Proble': 'Insurance Actionable',
  'Others': 'Insurance Actionable', 'REJECTED': 'Insurance Actionable',
  'Cancelled': 'Insurance Actionable', 'Cancel': 'Insurance Actionable',
  'On Hold in QC': 'Under QC', 'QC Pending': 'Under QC',
  'MEDICAL DONE & REPORT AWAITED': 'Under QC', 'Completed (QC Pending)': 'Under QC',
  'Call Done- Under QC': 'Under QC', 'Under QC': 'Under QC',
  'QC Done': 'Under QC', 'Report Awaited': 'Under QC',
};

function categorize(status) {
  if (!status) return 'Other';
  if (STATUS_CATEGORY[status]) return STATUS_CATEGORY[status];
  const lower = status.toLowerCase();
  if (lower.startsWith('call at ') || lower.startsWith('callback at ') || lower.startsWith('follow')) return 'WIP';
  return 'Other';
}

function elapsedMs(since) {
  return since ? Math.max(0, Date.now() - new Date(since).getTime()) : 0;
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── Animations ───────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Page = styled.div`
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #020617;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(2, 6, 23, 0.85);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.6rem;
  height: 3.5rem;
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.3rem;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  border-radius: 0.6rem;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #1e293b; color: #fff; }
`;

const NavTitle = styled.div`
  font-weight: 700;
  font-size: 1.4rem;
  color: #fff;
`;

const Badge = styled.span`
  font-size: 1rem;
  font-weight: 600;
  padding: 0.2rem 0.7rem;
  border-radius: 0.5rem;
  border: 1px solid;
  background: ${p => p.bg || 'rgba(245,158,11,0.1)'};
  border-color: ${p => p.border || 'rgba(245,158,11,0.3)'};
  color: ${p => p.color || '#fbbf24'};
`;

const Content = styled.div`
  max-width: 140rem;
  margin: 0 auto;
  padding: 1.6rem;
`;

const Card = styled.div`
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 1rem;
  overflow: hidden;
`;

const CardPad = styled(Card)`
  padding: 1.4rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.2rem;
  margin-bottom: 1.4rem;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatCard = styled(CardPad)`
  border-color: transparent;
  background: ${p => p.bg || '#1e293b'};
`;

const StatVal = styled.div`
  font-size: 2.8rem;
  font-weight: 900;
  color: ${p => p.color || '#fff'};
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 0.6rem;
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #1e293b;
  margin-bottom: 1.4rem;
  overflow-x: auto;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.9rem 1.4rem;
  border: none;
  background: none;
  font-size: 1.2rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${p => p.active ? '#fff' : '#64748b'};
  border-bottom: 2px solid ${p => p.active ? '#a78bfa' : 'transparent'};
  cursor: pointer;
  transition: color 0.15s;
  &:hover { color: #cbd5e1; }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 1rem;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 16rem;
`;

const SearchInput = styled.input`
  width: 100%;
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 0.8rem;
  padding: 0.7rem 1rem 0.7rem 3rem;
  font-size: 1.2rem;
  color: #fff;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #334155; }
  &::placeholder { color: #475569; }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #475569;
  display: flex;
`;

const FilterBtn = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 0.8rem;
  border: 1px solid ${p => p.active ? p.activeColor || '#a78bfa' : '#334155'};
  background: ${p => p.active ? (p.activeBg || 'rgba(167,139,250,0.15)') : '#0f172a'};
  color: ${p => p.active ? (p.activeColor || '#a78bfa') : '#64748b'};
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  text-transform: capitalize;
  &:hover { color: #fff; border-color: #64748b; }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(24rem, 1fr));
  gap: 1.2rem;
`;

const DoctorCardWrap = styled.div`
  background: #0f172a;
  border: 1px solid ${p =>
    p.status === 'online' ? 'rgba(52,211,153,0.25)' :
    p.status === 'break'  ? 'rgba(251,191,36,0.25)' :
    '#1e293b'};
  border-radius: 1rem;
  padding: 1.2rem;
  transition: border-color 0.2s;
  position: relative;
  &:hover .card-actions { opacity: 1; }
`;

const StatusDot = styled.span`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: ${p =>
    p.status === 'online' ? '#34d399' :
    p.status === 'break'  ? '#fbbf24' :
    '#475569'};
  display: inline-block;
  flex-shrink: 0;
  animation: ${p => p.status !== 'offline' ? pulse : 'none'} 2s infinite;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.6rem;
  padding-top: 0.8rem;
  border-top: 1px solid #1e293b;
  opacity: 0;
  transition: opacity 0.2s;
`;

const ActionBtn = styled.button`
  flex: ${p => p.grow ? 1 : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.8rem;
  border-radius: 0.6rem;
  border: none;
  background: #1e293b;
  color: #94a3b8;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #334155; color: #fff; }
`;

const DocDetailsBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  border-top: 1px solid rgba(255,255,255,0.05);
  padding: 0.6rem 0 0;
  margin-top: 0.6rem;
  font-size: 1.1rem;
  color: #475569;
  cursor: pointer;
  &:hover { color: #94a3b8; }
`;

const DocDetailsContent = styled.div`
  padding-top: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DocDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 1.1rem;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border-radius: 0.7rem;
  border: none;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnPrimary = styled(Btn)` background: #7c3aed; color: #fff; &:hover:not(:disabled) { background: #6d28d9; } `;
const BtnSuccess = styled(Btn)` background: rgba(52,211,153,0.15); color: #34d399; border: 1px solid rgba(52,211,153,0.3); &:hover:not(:disabled) { background: rgba(52,211,153,0.25); } `;
const BtnDanger  = styled(Btn)` background: rgba(248,113,113,0.1); color: #f87171; border: 1px solid rgba(248,113,113,0.3); &:hover:not(:disabled) { background: rgba(248,113,113,0.2); } `;
const BtnGhost   = styled(Btn)` background: #1e293b; color: #94a3b8; border: 1px solid #334155; &:hover { color: #fff; background: #334155; } `;
const BtnWarning = styled(Btn)` background: rgba(251,191,36,0.1); color: #fbbf24; border: 1px solid rgba(251,191,36,0.3); &:hover:not(:disabled) { background: rgba(251,191,36,0.2); } `;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.6rem;
  border: none;
  background: none;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #1e293b; color: #fff; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: ${p => p.right ? 'stretch' : 'center'};
  justify-content: ${p => p.right ? 'flex-end' : 'center'};
  z-index: 50;
  padding: ${p => p.right ? 0 : '1.6rem'};
`;

const ModalBox = styled.div`
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 1.2rem;
  padding: 2rem;
  width: 100%;
  max-width: ${p => p.wide ? '56rem' : '36rem'};
`;

const DrawerBox = styled.div`
  background: #0f172a;
  border-left: 1px solid #1e293b;
  width: 100%;
  max-width: 48rem;
  height: 100%;
  overflow-y: auto;
`;

const ModalTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.2rem;
`;

const ModalSub = styled.div`
  color: #94a3b8;
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
`;

const ModalRow = styled.div`
  display: flex;
  gap: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.8rem;
  padding: 0.9rem 1rem;
  font-size: 1.3rem;
  color: #fff;
  resize: none;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #a78bfa; }
  &::placeholder { color: #475569; }
`;

const Select = styled.select`
  width: 100%;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.8rem;
  padding: 0.8rem 1rem;
  font-size: 1.2rem;
  color: #fff;
  outline: none;
  &:focus { border-color: #a78bfa; }
`;

const TextInput = styled.input`
  width: 100%;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.8rem;
  padding: 0.8rem 1rem;
  font-size: 1.2rem;
  color: #fff;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #a78bfa; }
`;

const Label = styled.div`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.2rem;
`;

const Th = styled.th`
  text-align: ${p => p.center ? 'center' : 'left'};
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${p => p.color || '#64748b'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #1e293b;
  background: rgba(15,23,42,0.6);
`;

const Td = styled.td`
  padding: 0.9rem 1.2rem;
  border-bottom: 1px solid rgba(30,41,59,0.5);
  text-align: ${p => p.center ? 'center' : 'left'};
  font-variant-numeric: tabular-nums;
  &:last-child { border-bottom: none; }
`;

const InsurerName = styled.span`
  font-weight: 700;
  text-transform: capitalize;
  color: ${p => p.color || '#94a3b8'};
`;

const CountBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.6rem;
  font-weight: 700;
  font-size: 1.3rem;
  background: ${p => p.bg};
  color: ${p => p.color};
`;

const LiveTimer = ({ sinceRaw }) => {
  const [elapsed, setElapsed] = useState(() => elapsedMs(sinceRaw));
  useEffect(() => {
    if (!sinceRaw) return;
    const iv = setInterval(() => setElapsed(elapsedMs(sinceRaw)), 1000);
    return () => clearInterval(iv);
  }, [sinceRaw]);
  return <span>{fmtDuration(elapsed)}</span>;
};

// ─── Own Status Bar (admin checking their own attendance) ─────────────────────
function OwnStatusBar() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    attendanceApi.status().then(setStatus).catch(() => {});
  }, []);

  const state = (status && status.currentState) || 'offline';

  const doCheckIn = async () => {
    setLoading(true);
    try { await attendanceApi.checkIn(); setStatus(await attendanceApi.status()); } catch {}
    setLoading(false);
  };
  const doCheckOut = async () => {
    setLoading(true);
    try { await attendanceApi.checkOut(); setStatus(await attendanceApi.status()); } catch {}
    setLoading(false);
  };
  const doEndBreak = async () => {
    setLoading(true);
    try { await attendanceApi.endBreak(); setStatus(await attendanceApi.status()); } catch {}
    setLoading(false);
  };

  const dotColor = state === 'online' ? '#34d399' : state === 'break' ? '#fbbf24' : '#475569';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.4rem 1rem', borderRadius: '2rem', border: '1px solid #1e293b', background: '#0f172a', fontSize: '1.2rem' }}>
      <span style={{ width: '0.6rem', height: '0.6rem', borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
      <span style={{ color: '#cbd5e1', fontWeight: 600 }}>
        {state === 'online' ? "Online" : state === 'break' ? 'On Break' : 'Offline'}
      </span>
      {state === 'offline' && <button onClick={doCheckIn} disabled={loading} style={{ background: 'none', border: 'none', color: '#34d399', fontWeight: 700, cursor: 'pointer', fontSize: '1.2rem' }}>Check In</button>}
      {state === 'online'  && <button onClick={doCheckOut} disabled={loading} style={{ background: 'none', border: 'none', color: '#f87171', fontWeight: 700, cursor: 'pointer', fontSize: '1.2rem' }}>Check Out</button>}
      {state === 'break'   && <button onClick={doEndBreak} disabled={loading} style={{ background: 'none', border: 'none', color: '#34d399', fontWeight: 700, cursor: 'pointer', fontSize: '1.2rem' }}>Resume</button>}
    </div>
  );
}

// ─── DoctorCard ───────────────────────────────────────────────────────────────
function DoctorCard({ doctor, closureCount, onViewHistory, onChangeInsurer, onGrantAccess }) {
  const [expanded, setExpanded] = useState(false);
  const status = doctor.status || 'offline';
  const color = INSURER_COLORS[doctor.insurer] || '#64748b';
  const hasDetails = doctor.phone || doctor.email || doctor.panCard || doctor.qualification || doctor.regNumber || doctor.languages;

  return (
    <DoctorCardWrap status={status}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.8rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
          <StatusDot status={status} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.3rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doctor.name}</div>
            <div style={{ fontSize: '1rem', color: '#475569', fontFamily: 'monospace', marginTop: '0.2rem' }}>ID #{doctor.id}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 600, padding: '0.1rem 0.5rem', borderRadius: '0.4rem', textTransform: 'capitalize', background: color + '20', color }}>{doctor.insurer}</span>
              {doctor.qc && <span style={{ fontSize: '1rem', padding: '0.1rem 0.5rem', borderRadius: '0.4rem', background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}>QC</span>}
              {doctor.attendanceAdmin && <span style={{ fontSize: '1rem', padding: '0.1rem 0.5rem', borderRadius: '0.4rem', background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.2)' }}>Admin</span>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{closureCount != null ? closureCount : '—'}</div>
          <div style={{ fontSize: '1rem', color: '#475569' }}>cases</div>
        </div>
      </div>

      <div style={{ marginBottom: '0.8rem' }}>
        {status === 'online' && doctor.loginAtRaw && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem', color: '#34d399' }}>
            <FiClock size={11} /> Online <LiveTimer sinceRaw={doctor.loginAtRaw} />
            <span style={{ color: '#334155', marginLeft: 'auto', fontSize: '1rem' }}>{doctor.loginAt}</span>
          </div>
        )}
        {status === 'break' && doctor.breakStartRaw && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem', color: '#fbbf24' }}>
            ☕ Break <LiveTimer sinceRaw={doctor.breakStartRaw} />
          </div>
        )}
        {status === 'offline' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem', color: '#475569' }}>
            <FiWifiOff size={11} /> Not logged in
          </div>
        )}
      </div>

      {hasDetails && (
        <>
          <DocDetailsBtn onClick={() => setExpanded(v => !v)}>
            <span>Doctor details</span>
            <FiChevronDown size={12} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </DocDetailsBtn>
          {expanded && (
            <DocDetailsContent>
              {doctor.qualification && <DocDetailRow><span style={{ color: '#475569', flexShrink: 0 }}>🩺</span><span style={{ fontSize: '1.1rem', color: '#cbd5e1' }}>{doctor.qualification}</span></DocDetailRow>}
              {doctor.regNumber    && <DocDetailRow><FiCheckCircle size={11} color="#475569" /><span style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Reg: <span style={{ color: '#cbd5e1', fontFamily: 'monospace' }}>{doctor.regNumber}</span></span></DocDetailRow>}
              {doctor.phone        && <DocDetailRow><span style={{ color: '#475569', flexShrink: 0 }}>📞</span><span style={{ fontSize: '1.1rem', color: '#cbd5e1', fontFamily: 'monospace' }}>{doctor.phone}</span></DocDetailRow>}
              {doctor.email        && <DocDetailRow><span style={{ color: '#475569', flexShrink: 0 }}>✉</span><span style={{ fontSize: '1.1rem', color: '#cbd5e1', wordBreak: 'break-all' }}>{doctor.email}</span></DocDetailRow>}
              {doctor.panCard      && <DocDetailRow><span style={{ color: '#475569', flexShrink: 0 }}>🪪</span><span style={{ fontSize: '1.1rem', color: '#cbd5e1', fontFamily: 'monospace', letterSpacing: '0.05em' }}>{doctor.panCard}</span></DocDetailRow>}
              {doctor.languages    && <DocDetailRow><span style={{ color: '#475569', flexShrink: 0 }}>🌐</span><span style={{ fontSize: '1.1rem', color: '#cbd5e1' }}>{doctor.languages}</span></DocDetailRow>}
            </DocDetailsContent>
          )}
        </>
      )}

      <CardActions className="card-actions" style={{ marginTop: hasDetails ? '0.8rem' : '0.6rem' }}>
        <ActionBtn grow onClick={() => onViewHistory(doctor)}><FiEye size={12} /> History</ActionBtn>
        <ActionBtn onClick={() => onChangeInsurer(doctor)}><FiGrid size={12} /></ActionBtn>
        <ActionBtn onClick={() => onGrantAccess(doctor)}>
          {doctor.attendanceAdmin ? <FiShield size={12} color="#fbbf24" /> : <FiShield size={12} />}
        </ActionBtn>
      </CardActions>
    </DoctorCardWrap>
  );
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────
function Leaderboard({ doctors, closureMap }) {
  const ranked = [...doctors]
    .map(d => ({ ...d, count: closureMap[d.id] || 0 }))
    .sort((a, b) => b.count - a.count)
    .filter(d => d.count > 0);

  if (!ranked.length) return <div style={{ textAlign: 'center', color: '#475569', padding: '4rem 0' }}>No closures recorded yet today.</div>;

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', maxWidth: '60rem' }}>
      {ranked.map((d, i) => (
        <div key={d.id} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '0.9rem 1.2rem',
          borderRadius: '0.9rem',
          border: '1px solid',
          borderColor: i === 0 ? 'rgba(234,179,8,0.3)' : i === 1 ? '#334155' : i === 2 ? 'rgba(180,83,9,0.25)' : '#1e293b',
          background: i === 0 ? 'rgba(120,53,15,0.15)' : i === 1 ? 'rgba(30,41,59,0.5)' : i === 2 ? 'rgba(92,32,0,0.12)' : '#0f172a',
        }}>
          <div style={{ width: '2.4rem', textAlign: 'center', fontSize: '1.8rem', flexShrink: 0 }}>
            {medals[i] || <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.3rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
            <div style={{ fontSize: '1.1rem', color: INSURER_COLORS[d.insurer] || '#94a3b8' }}>{d.insurer}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <StatusDot status={d.status} />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: i === 0 ? '#eab308' : '#fff', fontVariantNumeric: 'tabular-nums' }}>{d.count}</div>
              <div style={{ fontSize: '1rem', color: '#475569' }}>cases</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Insurer Status Table ─────────────────────────────────────────────────────
function InsurerStatusTable({ doctors, availability }) {
  // Prefer the backend's dedicated aggregate (availability.byInsurer) which comes
  // from getAvailabilitySummary. Fall back to computing from the doctors list only
  // when that API hasn't returned yet.
  const byInsurer = (() => {
    if (availability && availability.byInsurer && availability.byInsurer.length) {
      return availability.byInsurer
        .filter(r => r.total > 0)
        .map(r => ({
          insurer: r.insurer,
          online:  parseInt(r.online)   || 0,
          onBreak: parseInt(r.on_break) || 0,
          offline: parseInt(r.offline)  || 0,
          total:   parseInt(r.total)    || 0,
        }))
        .sort((a, b) => b.total - a.total);
    }
    // Fallback: compute from individual doctor status
    return ALL_INSURERS.reduce((acc, ins) => {
      const group = doctors.filter(d => d.insurer === ins);
      if (!group.length) return acc;
      acc.push({
        insurer: ins,
        online:  group.filter(d => d.status === 'online').length,
        onBreak: group.filter(d => d.status === 'break').length,
        offline: group.filter(d => d.status === 'offline').length,
        total:   group.length,
      });
      return acc;
    }, []);
  })();

  const totals = byInsurer.reduce(
    (a, r) => ({ online: a.online + r.online, onBreak: a.onBreak + r.onBreak, offline: a.offline + r.offline, total: a.total + r.total }),
    { online: 0, onBreak: 0, offline: 0, total: 0 }
  );

  return (
    <div style={{ maxWidth: '70rem' }}>
      <div style={{ borderRadius: '1rem', border: '1px solid #1e293b', overflow: 'hidden' }}>
        <Table>
          <thead>
            <tr>
              <Th>Insurer</Th>
              <Th center color="#34d399"><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}><FiActivity size={12} />Online</div></Th>
              <Th center color="#fbbf24"><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>☕ Break</div></Th>
              <Th center><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}><FiWifiOff size={12} />Offline</div></Th>
              <Th center color="#cbd5e1">Total</Th>
            </tr>
          </thead>
          <tbody>
            {byInsurer.map(row => {
              const color = INSURER_COLORS[row.insurer] || '#64748b';
              const pct = row.total ? (row.online / row.total) * 100 : 0;
              return (
                <tr key={row.insurer} style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                      <span style={{ width: '0.65rem', height: '0.65rem', borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                      <InsurerName color={color}>{row.insurer}</InsurerName>
                      <div style={{ flex: 1, maxWidth: '8rem', height: '0.3rem', borderRadius: '0.3rem', background: '#1e293b', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color + 'aa', borderRadius: '0.3rem' }} />
                      </div>
                    </div>
                  </Td>
                  <Td center>
                    {row.online > 0
                      ? <CountBubble bg="rgba(52,211,153,0.1)" color="#34d399">{row.online}</CountBubble>
                      : <span style={{ color: '#334155', fontWeight: 600 }}>—</span>}
                  </Td>
                  <Td center>
                    {row.onBreak > 0
                      ? <CountBubble bg="rgba(251,191,36,0.1)" color="#fbbf24">{row.onBreak}</CountBubble>
                      : <span style={{ color: '#334155', fontWeight: 600 }}>—</span>}
                  </Td>
                  <Td center>
                    {row.offline > 0
                      ? <CountBubble bg="rgba(51,65,85,0.4)" color="#94a3b8">{row.offline}</CountBubble>
                      : <span style={{ color: '#334155', fontWeight: 600 }}>—</span>}
                  </Td>
                  <Td center><strong style={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{row.total}</strong></Td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #334155', background: 'rgba(30,41,59,0.4)' }}>
              <Td><strong style={{ color: '#cbd5e1', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em' }}>Total</strong></Td>
              <Td center><CountBubble bg="rgba(52,211,153,0.15)" color="#34d399"><strong>{totals.online}</strong></CountBubble></Td>
              <Td center><CountBubble bg="rgba(251,191,36,0.15)" color="#fbbf24"><strong>{totals.onBreak}</strong></CountBubble></Td>
              <Td center><CountBubble bg="rgba(51,65,85,0.5)" color="#94a3b8"><strong>{totals.offline}</strong></CountBubble></Td>
              <Td center><strong style={{ color: '#fff', fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums' }}>{totals.total}</strong></Td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
}

// ─── Daily Closures Panel ─────────────────────────────────────────────────────
function DailyClosuresPanel() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await adminApi.insurerClosures()); }
    catch { setError('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const rows = ALL_INSURERS.map(ins => {
    const found = (data && data.byInsurer) ? data.byInsurer.find(r => r.insurer === ins) : undefined;
    return { insurer: ins, count: found ? found.count : 0 };
  }).sort((a, b) => b.count - a.count);

  const total = rows.reduce((s, r) => s + r.count, 0);
  const maxCount = rows.length && rows[0].count > 0 ? rows[0].count : 1;

  return (
    <div style={{ maxWidth: '52rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.4rem' }}>
        <div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff' }}>Daily Closures</div>
          <div style={{ fontSize: '1.1rem', color: '#64748b', marginTop: '0.2rem' }}>
            {data ? `${data.date} · as of ${data.fetchedAt}` : 'Loading…'}
          </div>
        </div>
        <BtnGhost onClick={load} disabled={loading} style={{ padding: '0.5rem 1rem', fontSize: '1.1rem' }}>
          <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
        </BtnGhost>
      </div>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 1rem', borderRadius: '0.7rem', background: 'rgba(127,29,29,0.2)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontSize: '1.2rem', marginBottom: '1rem' }}>
          <FiAlertCircle size={14} /> {error}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        {rows.map(row => {
          const color = INSURER_COLORS[row.insurer] || '#64748b';
          const barPct = row.count > 0 ? Math.max(3, (row.count / maxCount) * 100) : 0;
          const active = row.count > 0;
          return (
            <div key={row.insurer} style={{
              display: 'flex', alignItems: 'center', gap: '1rem',
              padding: '0.8rem 1.1rem',
              borderRadius: '0.8rem',
              background: active ? color + '14' : '#0d1526',
              border: `1px solid ${active ? color + '40' : '#1e293b'}`,
            }}>
              <span style={{
                width: '0.65rem', height: '0.65rem', borderRadius: '50%',
                background: active ? color : '#475569',
                display: 'inline-block', flexShrink: 0,
              }} />
              <span style={{
                flex: '0 0 7.5rem',
                fontWeight: 700,
                textTransform: 'capitalize',
                fontSize: '1.3rem',
                color: active ? '#e2e8f0' : '#94a3b8',
              }}>{row.insurer}</span>
              <div style={{ flex: 1, height: '0.35rem', borderRadius: '0.35rem', background: '#1e293b', overflow: 'hidden' }}>
                {active && (
                  <div style={{ height: '100%', width: `${barPct}%`, background: color, borderRadius: '0.35rem', opacity: 0.75 }} />
                )}
              </div>
              <span style={{
                flex: '0 0 3rem', textAlign: 'right',
                fontWeight: 900,
                fontSize: '1.4rem',
                fontVariantNumeric: 'tabular-nums',
                color: active ? color : '#475569',
              }}>
                {active ? row.count : '—'}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: '1rem', padding: '0.8rem 1.1rem',
        borderRadius: '0.8rem', background: '#0f172a', border: '1px solid #334155',
      }}>
        <strong style={{ color: '#94a3b8', fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Cases Today</strong>
        <strong style={{ color: '#fff', fontSize: '2rem', fontVariantNumeric: 'tabular-nums' }}>{total}</strong>
      </div>
      <div style={{ fontSize: '1rem', color: '#64748b', textAlign: 'center', marginTop: '0.8rem' }}>
        Auto-refreshes every 60s · IST 00:00 → now · closed + approved today
      </div>
    </div>
  );
}

// ─── Case Status View ─────────────────────────────────────────────────────────
function CaseStatusView() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [days, setDays]       = useState(45);

  const load = useCallback(async (d) => {
    setLoading(true); setError(null);
    try { setData(await adminApi.misStatus(d)); }
    catch { setError('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [days]);

  const matrix = {};
  if (data) {
    for (const ins of ALL_INSURERS_IN_MIS) {
      matrix[ins] = { _detail: {} };
      for (const cat of CATEGORIES) { matrix[ins][cat] = 0; matrix[ins]._detail[cat] = []; }
      for (const { status, count } of (data.byInsurer[ins] || [])) {
        const cat = categorize(status);
        matrix[ins][cat] = (matrix[ins][cat] || 0) + count;
        matrix[ins]._detail[cat].push({ status, count });
      }
    }
  }

  const toggleExpand = (ins, cat) => {
    const key = `${ins}__${cat}`;
    setExpanded(prev => prev === key ? null : key);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.2rem' }}>
        <div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff' }}>Case Status Breakdown</div>
          <div style={{ fontSize: '1.1rem', color: '#475569', marginTop: '0.2rem' }}>Insurer-wise counts · last {days} days</div>
        </div>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <Select value={days} onChange={e => setDays(Number(e.target.value))} style={{ width: 'auto', padding: '0.5rem 1rem' }}>
            <option value={7}>Last 7 days</option>
            <option value={15}>Last 15 days</option>
            <option value={30}>Last 30 days</option>
            <option value={45}>Last 45 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
          <BtnGhost onClick={() => load(days)} disabled={loading} style={{ padding: '0.5rem 1rem', fontSize: '1.1rem' }}>
            <FiRefreshCw size={13} /> Refresh
          </BtnGhost>
        </div>
      </div>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 1rem', borderRadius: '0.7rem', background: 'rgba(127,29,29,0.2)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontSize: '1.2rem', marginBottom: '1rem' }}>
          <FiAlertCircle size={14} /> {error}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
        {CATEGORIES.map(cat => {
          const c = CAT_COLORS[cat];
          return (
            <span key={cat} style={{ fontSize: '1.1rem', padding: '0.3rem 0.8rem', borderRadius: '0.5rem', border: `1px solid ${c.border}`, background: c.bg, color: c.text, fontWeight: 600 }}>
              {cat}
            </span>
          );
        })}
      </div>
      <div style={{ overflowX: 'auto', borderRadius: '1rem', border: '1px solid #1e293b' }}>
        <Table style={{ minWidth: '80rem' }}>
          <thead>
            <tr>
              <Th style={{ position: 'sticky', left: 0, background: '#0f172a' }}>Insurer</Th>
              {CATEGORIES.map(cat => {
                const c = CAT_COLORS[cat];
                return <Th key={cat} center color={c.text}>{cat}</Th>;
              })}
              <Th center color="#cbd5e1">Total</Th>
            </tr>
          </thead>
          <tbody>
            {ALL_INSURERS_IN_MIS.map(ins => {
              const row = matrix[ins];
              if (!row) return null;
              const rowTotal = CATEGORIES.reduce((s, c) => s + (row[c] || 0), 0);
              const color = INSURER_COLORS[ins] || '#64748b';
              return (
                <React.Fragment key={ins}>
                  <tr style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}>
                    <Td style={{ position: 'sticky', left: 0, background: '#020617' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ width: '0.55rem', height: '0.55rem', borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                        <InsurerName color={color}>{ins}</InsurerName>
                      </div>
                    </Td>
                    {CATEGORIES.map(cat => {
                      const count = row[cat] || 0;
                      const key = `${ins}__${cat}`;
                      const isOpen = expanded === key;
                      const c = CAT_COLORS[cat];
                      return (
                        <Td key={cat} center>
                          {count > 0 ? (
                            <button
                              onClick={() => toggleExpand(ins, cat)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                                padding: '0.3rem 0.7rem',
                                borderRadius: '0.5rem',
                                border: `1px solid ${isOpen ? c.border : 'transparent'}`,
                                background: isOpen ? c.bg : 'transparent',
                                color: c.text,
                                fontWeight: 700,
                                fontSize: '1.2rem',
                                cursor: 'pointer',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {count}
                              <FiChevronDown size={11} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>
                          ) : (
                            <span style={{ color: '#334155' }}>—</span>
                          )}
                        </Td>
                      );
                    })}
                    <Td center><strong style={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{rowTotal || '—'}</strong></Td>
                  </tr>
                  {CATEGORIES.map(cat => {
                    const key = `${ins}__${cat}`;
                    if (expanded !== key) return null;
                    const detail = row._detail[cat] || [];
                    const c = CAT_COLORS[cat];
                    return (
                      <tr key={`${key}__d`} style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}>
                        <Td colSpan={CATEGORIES.length + 2} style={{ background: c.bg, paddingLeft: '2rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                            {detail.sort((a, b) => b.count - a.count).map(({ status, count }) => (
                              <span key={status} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem', padding: '0.3rem 0.7rem', borderRadius: '0.5rem', border: `1px solid ${c.border}`, background: '#020617' }}>
                                <span style={{ color: '#cbd5e1' }}>{status}</span>
                                <span style={{ fontWeight: 700, color: c.text }}>{count}</span>
                              </span>
                            ))}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          {data && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #334155', background: 'rgba(30,41,59,0.4)' }}>
                <Td style={{ position: 'sticky', left: 0, background: 'rgba(30,41,59,0.4)' }}><strong style={{ color: '#cbd5e1', fontSize: '1rem', textTransform: 'uppercase' }}>Total</strong></Td>
                {CATEGORIES.map(cat => {
                  const tot = ALL_INSURERS_IN_MIS.reduce((s, ins) => s + ((matrix[ins] ? matrix[ins][cat] : 0) || 0), 0);
                  const c = CAT_COLORS[cat];
                  return <Td key={cat} center><strong style={{ color: c.text, fontVariantNumeric: 'tabular-nums' }}>{tot || '—'}</strong></Td>;
                })}
                <Td center><strong style={{ color: '#fff', fontSize: '1.3rem', fontVariantNumeric: 'tabular-nums' }}>
                  {ALL_INSURERS_IN_MIS.reduce((s, ins) => s + CATEGORIES.reduce((cs, c) => cs + ((matrix[ins] ? matrix[ins][c] : 0) || 0), 0), 0)}
                </strong></Td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
      <div style={{ fontSize: '1rem', color: '#334155', textAlign: 'center', marginTop: '0.8rem' }}>
        Click any count to see breakdown · aegon = Bandhan Life · icicilbd = Lombard · icici = IPRU
      </div>
    </div>
  );
}

// ─── Broadcast Panel ──────────────────────────────────────────────────────────
function BroadcastPanel({ allMessages, onSent }) {
  const [msg, setMsg]               = useState('');
  const [targetInsurer, setTargetInsurer] = useState('');
  const [priority, setPriority]     = useState('normal');
  const [expiresInMinutes, setExpiresInMinutes] = useState('');
  const [sending, setSending]       = useState(false);
  const [sent, setSent]             = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await messagesApi.broadcast({ message: msg.trim(), targetInsurer: targetInsurer || null, priority, expiresInMinutes: expiresInMinutes || null });
      setMsg(''); setSent(true);
      setTimeout(() => setSent(false), 3000);
      if (onSent) onSent();
    } catch (e) { alert(e.message); }
    setSending(false);
  };

  return (
    <CardPad style={{ maxWidth: '48rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.2rem' }}>
        <FiMessageSquare color="#a78bfa" size={16} />
        <strong style={{ color: '#fff', fontSize: '1.4rem' }}>Broadcast Message</strong>
      </div>

      <FormGroup>
        <TextArea value={msg} onChange={e => setMsg(e.target.value)} rows={3} placeholder="Type a message to all doctors…" />
      </FormGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <FormGroup style={{ marginBottom: 0 }}>
          <Label>Priority</Label>
          <Select value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="urgent">Urgent 🚨</option>
          </Select>
        </FormGroup>
        <FormGroup style={{ marginBottom: 0 }}>
          <Label>Target Insurer</Label>
          <Select value={targetInsurer} onChange={e => setTargetInsurer(e.target.value)}>
            <option value="">All Insurers</option>
            {ALL_INSURERS.map(ins => <option key={ins} value={ins}>{ins}</option>)}
          </Select>
        </FormGroup>
      </div>

      <FormGroup>
        <Label>Expires in (minutes, optional)</Label>
        <TextInput type="number" value={expiresInMinutes} onChange={e => setExpiresInMinutes(e.target.value)} placeholder="e.g. 60" />
      </FormGroup>

      <BtnPrimary onClick={send} disabled={!msg.trim() || sending} style={{ width: '100%', justifyContent: 'center', marginBottom: (allMessages && allMessages.length) ? '1.2rem' : 0 }}>
        {sent ? <><FiCheckCircle size={14} /> Sent!</> : sending ? 'Sending…' : <><FiSend size={14} /> Send Broadcast</>}
      </BtnPrimary>

      {allMessages && allMessages.length > 0 && (
        <div style={{ borderTop: '1px solid #1e293b', paddingTop: '1rem' }}>
          <div style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '0.8rem' }}>Recent ({allMessages.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '16rem', overflowY: 'auto' }}>
            {allMessages.slice(0, 10).map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', padding: '0.6rem 0.8rem', borderRadius: '0.6rem', background: m.is_active ? '#1e293b' : '#0f172a', opacity: m.is_active ? 1 : 0.4 }}>
                <span style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: m.priority === 'urgent' ? '#f87171' : '#a78bfa', display: 'inline-block', flexShrink: 0, marginTop: '0.4rem' }} />
                <span style={{ flex: 1, fontSize: '1.2rem', color: '#cbd5e1', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{m.message}</span>
                {m.is_active && (
                  <button onClick={async () => { await messagesApi.deactivate(m.id); if (onSent) onSent(); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', flexShrink: 0 }}>
                    <FiX size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </CardPad>
  );
}

// ─── History Drawer ───────────────────────────────────────────────────────────
function HistoryDrawer({ doctor, onClose }) {
  const [data, setData]     = useState(null);
  const [prodDate, setProdDate] = useState(moment().format('YYYY-MM-DD'));
  const [prodData, setProdData] = useState(null);
  const [tab, setTab]       = useState('productivity');

  useEffect(() => {
    closuresApi.historyReport(doctor.id, 7).then(setData).catch(() => {});
  }, [doctor.id]);

  useEffect(() => {
    closuresApi.productivity(doctor.id, prodDate).then(setProdData).catch(() => {});
  }, [doctor.id, prodDate]);

  return (
    <Overlay right onClick={onClose}>
      <DrawerBox onClick={e => e.stopPropagation()}>
        <div style={{ position: 'sticky', top: 0, background: '#0f172a', borderBottom: '1px solid #1e293b', padding: '1.2rem 1.4rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.4rem' }}>{doctor.name}</div>
            <div style={{ fontSize: '1.1rem', color: INSURER_COLORS[doctor.insurer] || '#94a3b8' }}>{doctor.insurer}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.8rem', display: 'flex' }}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
          {[{ id: 'productivity', label: 'Day View' }, { id: 'history', label: '7-Day Report' }].map(({ id, label }) => (
            <Tab key={id} active={tab === id} onClick={() => setTab(id)}>{label}</Tab>
          ))}
        </div>

        <div style={{ padding: '1.4rem' }}>
          {tab === 'productivity' && (
            <>
              <FormGroup>
                <Label>Select Date</Label>
                <TextInput type="date" value={prodDate} max={moment().format('YYYY-MM-DD')} onChange={e => setProdDate(e.target.value)} />
              </FormGroup>
              {prodData ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                  <CardPad style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { l: 'First Login',    v: (prodData.summary && prodData.summary.firstLoginAt),                color: '#fff' },
                      { l: 'Last Logout',    v: (prodData.summary && prodData.summary.lastLogoutAt) || 'Still online', color: '#fff' },
                      { l: 'Total Login',    v: (prodData.summary && prodData.summary.totalLoginTime),               color: '#34d399' },
                      { l: 'Net Working',    v: (prodData.summary && prodData.summary.netWorkingTime),               color: '#a78bfa' },
                      { l: 'Break Time',     v: (prodData.summary && prodData.summary.totalBreakTime),               color: '#fbbf24' },
                      { l: 'Cases Closed',   v: (prodData.summary && prodData.summary.casesClosedToday),             color: '#a78bfa' },
                    ].map(({ l, v, color }) => (
                      <div key={l}>
                        <div style={{ fontSize: '1rem', color: '#475569' }}>{l}</div>
                        <div style={{ fontWeight: 700, color, fontSize: l === 'Cases Closed' ? '1.8rem' : '1.3rem' }}>{v != null ? v : '—'}</div>
                      </div>
                    ))}
                  </CardPad>
                  {prodData.sessions && prodData.sessions.map((s, i) => (
                    <CardPad key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
                        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Session {i + 1}</span>
                        <span style={{ fontSize: '1.1rem', color: '#475569' }}>{s.loginAt} → {s.logoutAt || 'Active'}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.8rem', marginBottom: (s.breaks && s.breaks.length) ? '0.8rem' : 0 }}>
                        {[
                          { l: 'Duration', v: s.sessionDuration,      color: '#fff' },
                          { l: 'Break',    v: s.totalBreakInSession,   color: '#fbbf24' },
                          { l: 'Net',      v: s.netWorkingInSession,   color: '#a78bfa' },
                        ].map(({ l, v, color }) => (
                          <div key={l} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', color: '#475569' }}>{l}</div>
                            <div style={{ fontWeight: 700, color }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {s.breaks && s.breaks.length > 0 && (
                        <div style={{ borderTop: '1px solid #1e293b', paddingTop: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          {s.breaks.map((b, j) => (
                            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                              <span style={{ color: '#fbbf24' }}>{b.start} → {b.end || 'ongoing'}</span>
                              <span style={{ color: '#94a3b8' }}>{b.duration}</span>
                              {b.reason && <span style={{ color: '#475569' }}>{b.reason}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardPad>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#475569', padding: '3rem 0' }}>Loading…</div>
              )}
            </>
          )}
          {tab === 'history' && data && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {data.report && data.report.map(d => (
                <CardPad key={d.date} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.8rem' }}>
                  <div>
                    <div style={{ fontSize: '1rem', color: '#475569' }}>Date</div>
                    <div style={{ fontWeight: 600, color: '#fff' }}>{moment(d.date).format('D MMM')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', color: '#475569' }}>Login</div>
                    <div style={{ color: '#34d399' }}>{d.totalLoginTime}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', color: '#475569' }}>Net</div>
                    <div style={{ fontWeight: 700, color: '#a78bfa' }}>{d.netWorkingTime}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', color: '#475569' }}>Cases</div>
                    <div style={{ fontWeight: 700, color: '#fff' }}>{d.casesClosedToday}</div>
                  </div>
                </CardPad>
              ))}
            </div>
          )}
        </div>
      </DrawerBox>
    </Overlay>
  );
}

// ─── Change Insurer Modal ─────────────────────────────────────────────────────
function ChangeInsurerModal({ doctor, onClose, onChanged }) {
  const [ins, setIns] = useState(doctor.insurer);
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    try { await adminApi.changeInsurer({ doctorId: doctor.id, insurer: ins }); onChanged(); onClose(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <Overlay>
      <ModalBox>
        <ModalTitle>Change Insurer</ModalTitle>
        <ModalSub>For <strong style={{ color: '#fff' }}>{doctor.name}</strong></ModalSub>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1.4rem', maxHeight: '24rem', overflowY: 'auto' }}>
          {ALL_INSURERS.map(i => (
            <button
              key={i}
              onClick={() => setIns(i)}
              style={{
                padding: '0.7rem', borderRadius: '0.7rem', border: `1px solid ${ins === i ? '#a78bfa' : '#334155'}`,
                background: ins === i ? 'rgba(167,139,250,0.15)' : '#1e293b',
                color: ins === i ? '#a78bfa' : '#94a3b8',
                fontSize: '1.2rem', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {i}
            </button>
          ))}
        </div>
        <ModalRow>
          <BtnGhost style={{ flex: 1 }} onClick={onClose}>Cancel</BtnGhost>
          <BtnPrimary style={{ flex: 1 }} onClick={confirm} disabled={loading || ins === doctor.insurer}>
            {loading ? 'Saving…' : 'Confirm'}
          </BtnPrimary>
        </ModalRow>
      </ModalBox>
    </Overlay>
  );
}

// ─── Grant Access Modal ───────────────────────────────────────────────────────
function GrantAccessModal({ doctor, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const willGrant = !doctor.attendanceAdmin;

  const confirm = async () => {
    setLoading(true);
    try { await adminApi.grantAccess({ doctorId: doctor.id, attendanceAdmin: willGrant ? 1 : 0 }); onChanged(); onClose(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <Overlay>
      <ModalBox>
        <div style={{ textAlign: 'center', marginBottom: '1.4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.6rem' }}>{willGrant ? '🛡️' : '⚠️'}</div>
          <ModalTitle style={{ marginBottom: '0.6rem', textAlign: 'center' }}>
            {willGrant ? 'Grant Admin Access' : 'Revoke Admin Access'}
          </ModalTitle>
          <ModalSub style={{ marginBottom: 0, textAlign: 'center' }}>
            {willGrant
              ? `${doctor.name} will be able to monitor attendance and send broadcasts.`
              : `${doctor.name} will lose attendance admin privileges.`}
          </ModalSub>
        </div>
        <ModalRow>
          <BtnGhost style={{ flex: 1 }} onClick={onClose}>Cancel</BtnGhost>
          {willGrant
            ? <BtnSuccess style={{ flex: 1 }} onClick={confirm} disabled={loading}>{loading ? 'Saving…' : 'Grant Access'}</BtnSuccess>
            : <BtnDanger  style={{ flex: 1 }} onClick={confirm} disabled={loading}>{loading ? 'Saving…' : 'Revoke Access'}</BtnDanger>}
        </ModalRow>
      </ModalBox>
    </Overlay>
  );
}

// ─── Main AdminView ───────────────────────────────────────────────────────────
export default function AdminView({ doctorFirstName, doctorId, insurer, history }) {
  const [doctors, setDoctors]       = useState([]);
  const [availability, setAvailability] = useState(null);
  const [closureMap, setClosureMap] = useState({});
  const [allMsgs, setAllMsgs]       = useState([]);
  const [selectedInsurer, setSelectedInsurer] = useState('');
  const [selectedStatus, setSelectedStatus]   = useState('');
  const [search, setSearch]         = useState('');
  const [mainTab, setMainTab]       = useState('team');
  const [historyDoctor, setHistoryDoctor]             = useState(null);
  const [changeInsurerDoctor, setChangeInsurerDoctor] = useState(null);
  const [grantAccessDoctor, setGrantAccessDoctor]     = useState(null);
  const pubnubRef = useRef(null);

  const fetchAll = useCallback(async () => {
    const [d, a, c, m] = await Promise.allSettled([
      adminApi.doctors(),
      adminApi.availability(),
      closuresApi.all(),
      messagesApi.all(),
    ]);
    if (d.status === 'fulfilled') setDoctors(d.value.doctors || []);
    if (a.status === 'fulfilled') setAvailability(a.value);
    if (c.status === 'fulfilled') setClosureMap(c.value.closures || {});
    if (m.status === 'fulfilled') setAllMsgs(m.value.messages || []);
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 60000);
    return () => clearInterval(iv);
  }, []);

  // PubNub for live status updates
  useEffect(() => {
    if (!config.pubnubSubscribeKey) return;
    const pn = new PubNub({
      subscribeKey: config.pubnubSubscribeKey,
      publishKey:   'demo',
      uuid:         `admin-attendance-${doctorId}-${Date.now()}`,
    });
    pn.addListener({
      message: ({ message: msg }) => {
        if (!msg || !msg.doctorId) return;
        setDoctors(prev => prev.map(d => {
          if (d.id !== msg.doctorId) return d;
          return {
            ...d,
            status: msg.status,
            loginAtRaw:    msg.status === 'online' ? (d.loginAtRaw || new Date().toISOString()) : null,
            breakStartRaw: msg.status === 'break'  ? new Date().toISOString() : null,
          };
        }));
      },
    });
    pn.subscribe({ channels: ['dr-dashboard-status'] });
    pubnubRef.current = pn;
    return () => { pn.unsubscribeAll(); };
  }, [doctorId]);

  // Filters
  const filteredDoctors = doctors.filter(d => {
    const matchInsurer = !selectedInsurer || d.insurer === selectedInsurer;
    const matchSearch  = !search || d.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus  = !selectedStatus || d.status === selectedStatus;
    return matchInsurer && matchSearch && matchStatus;
  });

  const presentInsurers = [...new Set(doctors.map(d => d.insurer))].sort();

  // Stats
  const baseForStats = selectedInsurer ? doctors.filter(d => d.insurer === selectedInsurer) : doctors;
  const stats = (availability && availability.overall) || {
    total: baseForStats.length,
    online: baseForStats.filter(d => d.status === 'online').length,
    on_break: baseForStats.filter(d => d.status === 'break').length,
    offline: baseForStats.filter(d => d.status === 'offline').length,
  };

  const TABS = [
    { id: 'team',       label: 'Team Monitor',   icon: FiUsers },
    { id: 'leaderboard',label: 'Leaderboard',     icon: FiAward },
    { id: 'insurer',    label: 'Insurer Status',  icon: FiBarChart2 },
    { id: 'daily',      label: 'Daily Closures',  icon: FiTrendingUp },
    { id: 'casestatus', label: 'Case Status',     icon: FiFilter },
    { id: 'broadcast',  label: 'Broadcast',       icon: FiBell },
  ];

  return (
    <Page>
      {/* Modals */}
      {historyDoctor      && <HistoryDrawer doctor={historyDoctor}           onClose={() => setHistoryDoctor(null)} />}
      {changeInsurerDoctor && <ChangeInsurerModal doctor={changeInsurerDoctor} onClose={() => setChangeInsurerDoctor(null)} onChanged={fetchAll} />}
      {grantAccessDoctor  && <GrantAccessModal   doctor={grantAccessDoctor}  onClose={() => setGrantAccessDoctor(null)} onChanged={fetchAll} />}

      {/* Header */}
      <TopNav>
        <NavLeft>
          <BackBtn onClick={() => history.push('/home')}><FiArrowLeft size={14} /> Back</BackBtn>
          <FiActivity color="#a78bfa" size={16} />
          <NavTitle>{doctorFirstName || 'Admin'}</NavTitle>
          <Badge>Attendance Admin</Badge>
        </NavLeft>
        <NavRight>
          <OwnStatusBar />
          <IconBtn onClick={fetchAll} title="Refresh"><FiRefreshCw size={16} /></IconBtn>
        </NavRight>
      </TopNav>

      <Content>
        {/* Stats */}
        <StatsGrid>
          <StatCard bg="#1e293b">
            <StatLabel>Total Doctors</StatLabel>
            <StatVal color="#cbd5e1">{stats.total != null ? stats.total : '—'}</StatVal>
          </StatCard>
          <StatCard bg="rgba(5,150,105,0.08)">
            <StatLabel style={{ color: '#34d399' }}>Online</StatLabel>
            <StatVal color="#34d399">{stats.online != null ? stats.online : '—'}</StatVal>
          </StatCard>
          <StatCard bg="rgba(120,53,15,0.1)">
            <StatLabel style={{ color: '#fbbf24' }}>On Break</StatLabel>
            <StatVal color="#fbbf24">{stats.on_break != null ? stats.on_break : '—'}</StatVal>
          </StatCard>
          <StatCard bg="#0f172a">
            <StatLabel>Offline</StatLabel>
            <StatVal color="#475569">{stats.offline != null ? stats.offline : '—'}</StatVal>
          </StatCard>
        </StatsGrid>

        {/* Tabs */}
        <TabBar>
          {TABS.map(({ id, label, icon: Icon }) => (
            <Tab key={id} active={mainTab === id} onClick={() => setMainTab(id)}>
              <Icon size={14} /> {label}
            </Tab>
          ))}
        </TabBar>

        {/* Team Monitor */}
        {mainTab === 'team' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 34rem', gap: '1.6rem' }}>
            <div>
              {/* Filters */}
              <FilterRow>
                <SearchWrap>
                  <SearchIcon><FiSearch size={14} /></SearchIcon>
                  <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doctors…" />
                </SearchWrap>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <FilterBtn active={!selectedInsurer} onClick={() => { setSelectedInsurer(''); setSelectedStatus(''); }}>All</FilterBtn>
                  {presentInsurers.map(ins => (
                    <FilterBtn
                      key={ins}
                      active={selectedInsurer === ins}
                      activeColor={INSURER_COLORS[ins]}
                      activeBg={(INSURER_COLORS[ins] || '#7c3aed') + '25'}
                      onClick={() => { setSelectedInsurer(ins === selectedInsurer ? '' : ins); setSelectedStatus(''); }}
                    >
                      {ins}
                    </FilterBtn>
                  ))}
                </div>
              </FilterRow>

              {/* Status sub-filters */}
              {(() => {
                const base = doctors.filter(d =>
                  (!selectedInsurer || d.insurer === selectedInsurer) &&
                  (!search || d.name.toLowerCase().includes(search.toLowerCase()))
                );
                const onlineCount = base.filter(d => d.status === 'online').length;
                const breakCount  = base.filter(d => d.status === 'break').length;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.1rem', color: '#475569' }}>Showing {filteredDoctors.length} of {base.length} doctors</span>
                    <FilterBtn
                      active={selectedStatus === 'online'}
                      activeColor="#34d399" activeBg="rgba(52,211,153,0.15)"
                      onClick={() => setSelectedStatus(selectedStatus === 'online' ? '' : 'online')}
                    >
                      <FiActivity size={11} style={{ marginRight: '0.3rem' }} />{onlineCount} Online
                    </FilterBtn>
                    <FilterBtn
                      active={selectedStatus === 'break'}
                      activeColor="#fbbf24" activeBg="rgba(251,191,36,0.12)"
                      onClick={() => setSelectedStatus(selectedStatus === 'break' ? '' : 'break')}
                    >
                      ☕ {breakCount} On Break
                    </FilterBtn>
                    {selectedStatus && (
                      <FilterBtn onClick={() => setSelectedStatus('')}><FiX size={11} /> All</FilterBtn>
                    )}
                  </div>
                );
              })()}

              {filteredDoctors.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#475569', padding: '6rem 0' }}>No doctors found.</div>
              ) : (
                <DoctorGrid>
                  {filteredDoctors.map(doctor => (
                    <DoctorCard
                      key={doctor.id}
                      doctor={doctor}
                      closureCount={closureMap[doctor.id]}
                      onViewHistory={setHistoryDoctor}
                      onChangeInsurer={setChangeInsurerDoctor}
                      onGrantAccess={setGrantAccessDoctor}
                    />
                  ))}
                </DoctorGrid>
              )}
            </div>
            <div>
              <BroadcastPanel allMessages={allMsgs} onSent={fetchAll} />
            </div>
          </div>
        )}

        {mainTab === 'leaderboard' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff' }}>Today's Leaderboard</div>
              <span style={{ fontSize: '1.1rem', color: '#475569' }}>{moment().format('D MMM YYYY')}</span>
            </div>
            <Leaderboard doctors={doctors} closureMap={closureMap} />
          </div>
        )}

        {mainTab === 'insurer' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff' }}>Live Insurer Status</div>
              <span style={{ fontSize: '1.1rem', color: '#475569' }}>All insurers · updates every 60s</span>
            </div>
            <InsurerStatusTable doctors={doctors} availability={availability} />
          </div>
        )}

        {mainTab === 'daily' && <DailyClosuresPanel />}

        {mainTab === 'casestatus' && <CaseStatusView />}

        {mainTab === 'broadcast' && <BroadcastPanel allMessages={allMsgs} onSent={fetchAll} />}
      </Content>
    </Page>
  );
}
